import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ApiService } from 'app/shared/apis/api.service';
import { ToastService } from './toast.service';
import { ImageLightboxDialogComponent } from '../dialogs/image-lightbox-dialog/image-lightbox-dialog.component';
import { take } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ImageViewerService {
    // Cache de URLs já obtidas (chave normalizada -> URL presignada)
    private urlCache: Map<string, string> = new Map();
    // Cache de requisições em andamento para evitar requisições duplicadas simultâneas
    private pendingRequests: Map<string, Promise<string | null>> = new Map();

    // Limita quantas chamadas a storage/download-url saem ao mesmo tempo. Quando uma tela
    // renderiza muitas imagens de uma vez (ex.: abrir um painel com vários produtos), evita
    // disparar uma rajada de requisições simultâneas para o backend.
    private static readonly MAX_CONCURRENT_REQUESTS = 2;
    private activeRequests = 0;
    private requestQueue: Array<() => void> = [];

    private async acquireSlot(): Promise<void> {
        if (this.activeRequests < ImageViewerService.MAX_CONCURRENT_REQUESTS) {
            this.activeRequests++;
            return;
        }
        await new Promise<void>((resolve) => this.requestQueue.push(resolve));
        this.activeRequests++;
    }

    private releaseSlot(): void {
        this.activeRequests--;
        const next = this.requestQueue.shift();
        if (next) {
            next();
        }
    }

    constructor(
        private dialog: MatDialog,
        private api: ApiService,
        private toast: ToastService,
        private router: Router,
    ) {}

    /**
     * Normaliza a chave do S3 removendo o domínio
     */
    private normalizeS3Key(url: string): string {
        if (!url) return url;

        // Se já é uma chave simples (sem http), retornar como está
        if (!url.startsWith('http')) {
            return url;
        }

        const knownPrefixes = [
            'https://evobrides.s3.amazonaws.com/',
            'https://evobrides.s3.sa-east-1.amazonaws.com/',
            'https://art-sys.s3.amazonaws.com/',
            'https://art-sys.s3.sa-east-1.amazonaws.com/',
            'https://cdn.artsys.com/',
        ];
        for (const prefix of knownPrefixes) {
            if (url.startsWith(prefix)) {
                return url.slice(prefix.length);
            }
        }

        try {
            const parsed = new URL(url);
            if (parsed.hostname === 'cdn.artsys.com' || (parsed.hostname.includes('s3') && parsed.hostname.endsWith('amazonaws.com'))) {
                return decodeURIComponent(parsed.pathname.replace(/^\/+/, ''));
            }
        } catch {
            // ignore
        }

        return url;
    }

    /**
     * Verifica se a URL é do S3
     */
    private isS3Url(url: string): boolean {
        return (
            url.includes('s3.amazonaws.com') ||
            url.includes('cdn.artsys.com') ||
            url.includes('evobrides') ||
            (!url.startsWith('http') && !url.startsWith('data:'))
        );
    }

    /**
     * Obtém URL válida da imagem (com fallback para S3 se necessário)
     */
    async getImageUrl(imageUrl: string | null, options?: { bypassCache?: boolean }): Promise<string | null> {
        if (!imageUrl) return null;

        // Se não é URL do S3, retornar como está
        if (!this.isS3Url(imageUrl)) {
            return imageUrl;
        }

        const normalizedKey = this.normalizeS3Key(imageUrl);

        // Verificar se já está no cache
        if (!options?.bypassCache && this.urlCache.has(normalizedKey)) {
            return this.urlCache.get(normalizedKey)!;
        }

        // Verificar se há uma requisição em andamento para a mesma URL
        if (!options?.bypassCache && this.pendingRequests.has(normalizedKey)) {
            return this.pendingRequests.get(normalizedKey)!;
        }

        if (options?.bypassCache) {
            this.urlCache.delete(normalizedKey);
        }

        // Criar nova requisição
        const requestPromise = (async (): Promise<string | null> => {
            await this.acquireSlot();
            try {
                const response = await this.api
                    .postSub<{ url: string }, { urlOrKey: string }>('storage/download-url', { urlOrKey: normalizedKey })
                    .pipe(take(1))
                    .toPromise();

                const presignedUrl = response?.url || imageUrl;

                // Armazenar no cache
                this.urlCache.set(normalizedKey, presignedUrl);

                return presignedUrl;
            } catch (error) {
                console.error('Erro ao obter URL da imagem:', error);
                // Não armazenar erro no cache, retornar URL original
                return imageUrl;
            } finally {
                this.releaseSlot();
                // Remover da lista de requisições pendentes
                this.pendingRequests.delete(normalizedKey);
            }
        })();

        // Armazenar a promise em andamento
        this.pendingRequests.set(normalizedKey, requestPromise);

        return requestPromise;
    }

    /**
     * Abre a imagem/PDF na rota interna /file-viewer (nova aba).
     * Evita ir direto ao S3, que força download por Content-Disposition.
     */
    openInNewTab(imageUrl: string | null): void {
        if (!imageUrl) {
            this.toast.warning('URL da imagem não disponível');
            return;
        }

        const tree = this.router.createUrlTree(['/file-viewer'], {
            queryParams: { src: imageUrl },
        });
        const url = this.router.serializeUrl(tree);
        const opened = window.open(url, '_blank');
        if (!opened) {
            this.toast.warning('Permita pop-ups para abrir o arquivo no navegador');
        }
    }

    /**
     * Abre a imagem no navegador (inline) em vez de forçar download
     */
    downloadImage(imageUrl: string | null, _fileName?: string): void {
        this.openInNewTab(imageUrl);
    }

    /**
     * Abre a imagem em um modal lightbox para visualização ampliada
     */
    openLightbox(imageUrl: string | null, images?: string[], currentIndex?: number): void {
        if (!imageUrl) {
            this.toast.warning('URL da imagem não disponível');
            return;
        }

        const dialogRef = this.dialog.open(ImageLightboxDialogComponent, {
            data: {
                imageUrl,
                images: images || [imageUrl],
                currentIndex: currentIndex ?? (images ? images.indexOf(imageUrl) : 0),
            },
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '100%',
            height: '100%',
            panelClass: 'image-lightbox-dialog',
        });

        dialogRef.afterClosed().subscribe(() => {
            // Dialog fechado
        });
    }

    /**
     * Limpa o cache de URLs (útil para testes ou quando arquivos são atualizados)
     */
    clearCache(): void {
        this.urlCache.clear();
        this.pendingRequests.clear();
    }
}

